import {useEffect,useRef,useState,type FormEvent} from 'react';

export type CreatedCustomer={
  customerId:string;displayName:string;homeLocationCode:string|null;
  actorId?:string|null;email?:string|null;phone?:string|null;
};
type CustomerResponse={
  ok:boolean;
  data?:{status:string;customerId?:string;displayName?:string;matches?:CreatedCustomer[]};
  error?:{message?:string};
};
type Preferences={
  appointmentConfirmation:boolean;appointmentReminder:boolean;
  saleReceipt:boolean;nonEssential:boolean;mailingList:boolean;
};
type Props={
  locationCode:string;onClose:()=>void;
  onCreated:(customer:CreatedCustomer)=>void;
  mode?:'full'|'booking';
  initial?:{firstName?:string;lastName?:string;email?:string;phone?:string;staffActorId?:string};
};
const initialPreferences:Preferences={
  appointmentConfirmation:true,appointmentReminder:true,saleReceipt:true,
  nonEssential:true,mailingList:true,
};

/** The Sales menu opens the full Helm-style account window.
 * A Schedule slot opens an abbreviated inline four-field form.
 * Both write through the same permission-scoped, audited creation route.
 */
export function NewCustomerDialog({locationCode,onClose,onCreated,mode='full',initial}:Props){
  const [firstName,setFirstName]=useState(initial?.firstName??'');
  const [lastName,setLastName]=useState(initial?.lastName??'');
  const [email,setEmail]=useState(initial?.email??'');
  const [phone,setPhone]=useState(initial?.phone??'');
  const [smsOptIn,setSmsOptIn]=useState(true);
  const [preferences,setPreferences]=useState<Preferences>(initialPreferences);
  const [birthMonth,setBirthMonth]=useState('');
  const [birthDay,setBirthDay]=useState('');
  const [birthYear,setBirthYear]=useState('');
  const [address,setAddress]=useState('');
  const [postalCode,setPostalCode]=useState('');
  const [emergencyOpen,setEmergencyOpen]=useState(false);
  const [emergencyName,setEmergencyName]=useState('');
  const [emergencyPhone,setEmergencyPhone]=useState('');
  const [heardFrom,setHeardFrom]=useState('');
  const [heardOptions,setHeardOptions]=useState<string[]>([]);
  useEffect(()=>{
    const controller=new AbortController();
    setHeardOptions([]);
    void fetch('/api/v1/public/signup-options?'+new URLSearchParams({
      location:locationCode,
    }),{signal:controller.signal,credentials:'same-origin',cache:'no-store'})
      .then(async response=>{
        if(!response.ok)throw Error('Signup options unavailable');
        const body=await response.json() as {ok:boolean;data?:{referralOptions?:string[]}};
        if(body.ok&&Array.isArray(body.data?.referralOptions)&&!controller.signal.aborted)
          setHeardOptions(body.data.referralOptions.filter(value=>typeof value==='string'));
      }).catch(()=>{ /* Never invent choices when source configuration cannot load. */ });
    return ()=>controller.abort();
  },[locationCode]);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [matches,setMatches]=useState<CreatedCustomer[]|null>(null);
  const [contactConflict,setContactConflict]=useState(false);
  const idempotencyKey=useRef<string|null>(null);
  const name=[firstName.trim(),lastName.trim()].filter(Boolean).join(' ');
  const phoneDigits=phone.replace(/\D/g,'');
  // Preserve prototype behavior: any one identifying field may create a
  // guest, not an invented requirement for both name fields.
  const hasIdentifier=Boolean(name||email.trim()||phoneDigits);
  const fullName=name||email.trim().toLowerCase()||
    (phoneDigits?'Guest • '+phoneDigits.slice(-4):'');
  const change=()=>{setMatches(null);setContactConflict(false);setError('');idempotencyKey.current=null;};
  const create=async(event?:FormEvent<HTMLFormElement>,reviewed?:string[])=>{
    event?.preventDefault();
    if(busy)return;
    if(!hasIdentifier){setError('Enter at least one identifying field.');return;}
    setBusy(true);setError('');
    if(!idempotencyKey.current)idempotencyKey.current=crypto.randomUUID()+'-sales-new-customer';
    try{
      const registration=mode==='full'?{
        firstName:firstName.trim(),lastName:lastName.trim(),
        birthdayMonth:birthMonth?Number(birthMonth):null,
        birthdayDay:birthDay?Number(birthDay):null,
        birthdayYear:birthYear?Number(birthYear):null,
        address:address.trim()||null,postalCode:postalCode.trim()||null,
        emergencyContactName:emergencyName.trim()||null,
        emergencyContactPhone:emergencyPhone.trim()||null,
        heardFrom:heardFrom.trim()||null,emailPreferences:preferences,
      }:undefined;
      const response=await fetch('/api/v1/staff/customers',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:locationCode,displayName:fullName,
          firstName:firstName.trim(),lastName:lastName.trim(),
          ...(email.trim()?{email:email.trim()}:{}),
          ...(phone.trim()?{phone:phone.trim()}:{}),
          smsOptIn,
          ...(initial?.staffActorId?{staffActorId:initial.staffActorId}:{}),
          ...(registration?{registration}:{}),
          idempotencyKey:idempotencyKey.current,
          ...(reviewed?{reviewedExistingCustomerIds:reviewed}:{}),
        }),
      });
      const json=await response.json() as CustomerResponse;
      if(response.status===409&&
        (json.data?.status==='review_required'||json.data?.status==='duplicate_contact')){
        setMatches(json.data.matches??[]);
        setContactConflict(json.data.status==='duplicate_contact');
        idempotencyKey.current=null;
        return;
      }
      if(!response.ok||!json.ok||!json.data?.customerId)
        throw new Error(json.error?.message??'Customer could not be created.');
      onCreated({
        customerId:json.data.customerId,
        displayName:json.data.displayName??fullName,
        homeLocationCode:locationCode,
      });
    }catch(e){
      setError(e instanceof Error?e.message:'Customer could not be created.');
    }finally{setBusy(false);}
  };
  const fields=<>
    <label>First Name
      <input autoFocus maxLength={70} aria-label="First Name"
        value={firstName} disabled={busy}
        onChange={event=>{setFirstName(event.target.value);change();}}/>
    </label>
    <label>Last Name
      <input maxLength={70} aria-label="Last Name"
        value={lastName} disabled={busy}
        onChange={event=>{setLastName(event.target.value);change();}}/>
    </label>
    <label>Phone
      <input type="tel" aria-label="Phone" value={phone}
        disabled={busy} onChange={event=>{setPhone(event.target.value);change();}}/>
    </label>
    <label className="ecfs-create-text-opt">
      <input type="checkbox" checked={smsOptIn} disabled={busy}
        onChange={event=>{setSmsOptIn(event.target.checked);change();}}/>
      Wants to Receive Text Messages
    </label>
    <label>Email
      <input type="email" aria-label="Email" value={email}
        disabled={busy} onChange={event=>{setEmail(event.target.value);change();}}/>
    </label>
  </>;
  const duplicates=matches&&matches.length>0&&<div aria-label="Matching existing customers">
    <h3>{contactConflict?'An existing customer already uses this email or phone':
      'Existing customers with matching details'}</h3>
    {matches.map(item=><button type="button" key={item.customerId}
      onClick={()=>onCreated(item)}>{item.displayName}</button>)}
    {!contactConflict&&<button type="button" disabled={busy}
      onClick={()=>void create(undefined,matches.map(item=>item.customerId))}>
      Create a different customer
    </button>}
  </div>;
  if(mode==='booking')return <form className="ecfs-quick-customer-form"
    aria-label="Abbreviated new customer" onSubmit={event=>void create(event)}>
      <div className="ecfs-quick-name-row">
        <label>First Name<input maxLength={70} aria-label="First Name"
          value={firstName} onChange={e=>{setFirstName(e.target.value);change();}}/></label>
        <label>Last Name<input maxLength={70} aria-label="Last Name"
          value={lastName} onChange={e=>{setLastName(e.target.value);change();}}/></label>
      </div>
      <div className="ecfs-quick-contact-row">
        <label>Email<input type="email" aria-label="Email" value={email}
          onChange={e=>{setEmail(e.target.value);change();}}/></label>
        <label>Phone<input type="tel" aria-label="Phone" value={phone}
          onChange={e=>{setPhone(e.target.value);change();}}/></label>
      </div>
      <label className="ecfs-create-text-opt"><input type="checkbox" checked={smsOptIn}
        onChange={e=>{setSmsOptIn(e.target.checked);change();}}/>
        Wants to Receive Text Messages</label>
      <div className="ecfs-quick-actions">
        <button type="submit" disabled={busy||!hasIdentifier}>
          {busy?'Saving…':'Add And Book'}</button>
        <span>or <button type="button" className="ecfs-customer-link" onClick={onClose}>Search</button></span>
      </div>
      {duplicates}{error&&<p role="alert">{error}</p>}
    </form>;

  const preference=(key:keyof Preferences,label:string)=>
    <label className="ecfs-customer-check" key={key}>
      <input type="checkbox" checked={preferences[key]}
        onChange={e=>{setPreferences(v=>({...v,[key]:e.target.checked}));change();}}/>
      {label}
    </label>;
  return <div className="ecfs-customer-create-backdrop">
    <section role="dialog" aria-modal="true" aria-label="New Customer Account"
      className="ecfs-customer-create-dialog">
      <header><h2>New Customer Account</h2>
        <button type="button" aria-label="Close New Customer" onClick={onClose}>×</button>
      </header>
      <form onSubmit={event=>void create(event)}>
        <div className="ecfs-customer-create-scroll">
          {fields}
          {preference('appointmentConfirmation','Wants Appt Confirmation emails')}
          {preference('appointmentReminder','Wants Appt Reminder emails')}
          {preference('saleReceipt','Wants Sale Receipt emails')}
          {preference('nonEssential','Wants Non-essential emails')}
          {preference('mailingList','Wants Mailing List emails')}
          <div className="ecfs-customer-birthday">
            <span>Birthday</span>
            <select aria-label="Birthday month" value={birthMonth}
              onChange={e=>{setBirthMonth(e.target.value);change();}}>
              <option value="">month</option>
              {['January','February','March','April','May','June','July','August',
                'September','October','November','December'].map((m,i)=>
                <option value={i+1} key={m}>{m}</option>)}
            </select>
            <input aria-label="Birthday day" type="number" min={1} max={31}
              placeholder="day" value={birthDay}
              onChange={e=>{setBirthDay(e.target.value);change();}}/>
            <input aria-label="Birthday year" type="number" min={1900} max={2100}
              placeholder="year (optional)" value={birthYear}
              onChange={e=>{setBirthYear(e.target.value);change();}}/>
          </div>
          <label>Address<input aria-label="Address" value={address}
            onChange={e=>{setAddress(e.target.value);change();}}/></label>
          <label>Postal Code<input aria-label="Postal Code" value={postalCode}
            onChange={e=>{setPostalCode(e.target.value);change();}}/></label>
          <button type="button" className="ecfs-customer-link"
            onClick={()=>setEmergencyOpen(v=>!v)}>
            {emergencyOpen?'Hide Emergency Contact Info':'Add Emergency Contact Info'}
          </button>
          {emergencyOpen&&<div className="ecfs-customer-emergency">
            <label>Emergency Contact Name<input aria-label="Emergency Contact Name"
              value={emergencyName} onChange={e=>{setEmergencyName(e.target.value);change();}}/></label>
            <label>Emergency Contact Phone<input aria-label="Emergency Contact Phone"
              value={emergencyPhone} onChange={e=>{setEmergencyPhone(e.target.value);change();}}/></label>
          </div>}
          <label>How did you hear about us?
            <select aria-label="How did you hear about us?" value={heardFrom}
              onChange={e=>{setHeardFrom(e.target.value);change();}}>
              <option value="">Select</option>
              {heardOptions.map(option=><option key={option} value={option}>{option}</option>)}
            </select>
          </label>
        </div>
        <div className="ecfs-customer-create-actions">
          <button type="submit" disabled={busy||!hasIdentifier}>
            {busy?'Saving…':'Save'}</button>
        </div>
      </form>
      {duplicates}{error&&<p role="alert">{error}</p>}
    </section>
  </div>;
}
